/**
 * The typed fetch wrapper. Every request in either app goes through here.
 *
 * What it guarantees:
 *   - `Authorization: Bearer <token>` from the app's session store
 *   - `X-Correlation-Id` on every request, so a UI failure can be found in the
 *     backend audit log (see `kernel/api/exceptions.py`, which echoes it back)
 *   - `application/problem+json` bodies become the right `ApiError` subclass
 *   - `If-Match` for optimistic concurrency, `Idempotency-Key` where the spec
 *     requires it (conversion, agreement acceptance, payment verification,
 *     invoice issue, refund approval — Step 3 Spec §5)
 *   - a single in-flight refresh, shared by every request that hits 401
 *
 * It does NOT decide what the user sees. That is `userMessage()` in `errors.ts`.
 */

import { ApiError, NetworkError, toApiError, UnauthenticatedError } from './errors';
import { isExpiring, type SessionStore, type StoredSession } from './session';

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body. Use `formData` instead for uploads. */
  body?: unknown;
  formData?: FormData;
  /** Allowlisted filters and sorts only — the backend rejects anything else. */
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Optimistic concurrency. Pass the aggregate's `version` from the record you
   * loaded. A stale write comes back 409, never a silent overwrite.
   */
  ifMatch?: string | number;
  /**
   * REQUIRED by the API on: conversion, agreement acceptance, payment
   * verification, invoice issue, refund approval. A replay with the same key
   * returns the stored response; a replay with a different body is 409.
   */
  idempotencyKey?: string;
  /** Skip the bearer header. Only login and refresh should set this. */
  anonymous?: boolean;
  /**
   * The path is an EXACT, ABSOLUTE route — one registered with
   * `registry.add_path` rather than generated by a router.
   *
   * Two things follow, and they always travel together, which is why this is one
   * flag rather than two:
   *   - `baseUrl` is NOT prepended (these paths are absolute: `/api/staff/me`,
   *     `/api/auth/staff/login`)
   *   - NO trailing slash is added (they are mounted exactly as written)
   *
   * See `EXACT_PATHS` in `endpoints.ts`. The helpers in `auth.ts` set this for you.
   */
  exact?: boolean;
  /**
   * Override the trailing-slash rule on its own. Rarely needed — `exact` is what
   * you want for registered paths. Router resources should never set this.
   */
  trailingSlash?: boolean;
  /**
   * Return a successful response's body as a Blob instead of parsing it. For
   * endpoints that may stream a file; reading those as text corrupts the bytes.
   * Errors are still parsed and thrown as usual.
   */
  asBlob?: boolean;
  /** Internal: prevents a refresh loop. */
  _retried?: boolean;
}

export interface ApiClientConfig {
  /** e.g. `/api/staff` or `https://api.example.com/api/staff`. No trailing slash. */
  baseUrl: string;
  session: SessionStore;
  /**
   * Exchange the stored session for a fresh one. Return null when refresh is
   * impossible, and the client will treat the request as unauthenticated.
   * Omit entirely if the surface has no refresh endpoint.
   */
  refresh?: (current: StoredSession) => Promise<StoredSession | null>;
  /**
   * Called once when the session is definitively gone. The app clears state and
   * routes to login. Called at most once per failed refresh, not per request.
   */
  onUnauthenticated?: () => void;
  /** Injected in tests. */
  fetchImpl?: typeof fetch;
}

function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Every router-generated route on this API ENDS IN A TRAILING SLASH:
 * `/api/staff/cases/`, `/api/staff/cases/{id}/`, `/api/staff/cases/{id}/transition/`.
 *
 * `APPEND_SLASH` is OFF on the backend, deliberately. Django's default answers a
 * near-miss with a 301 to the slashed form, which for a browser is a courtesy and
 * for an API is a data-loss bug — a redirected POST arrives as a GET with no body.
 * A missing slash is therefore a hard 404 with no forgiveness.
 *
 * So the slash is added HERE rather than trusted to two hundred call sites. Add a
 * path without one and it still works; add one with a query string already attached
 * and this will refuse to mangle it.
 */
export function normalisePath(path: string, trailingSlash = true): string {
  const withLeading = path.startsWith('/') ? path : `/${path}`;
  if (withLeading.includes('?')) {
    throw new Error(
      `Query strings belong in the "query" option, not in the path: ${path}. ` +
        'Putting them in the path defeats the trailing-slash normalisation.',
    );
  }
  if (!trailingSlash) return withLeading.replace(/\/$/, '');
  return withLeading.endsWith('/') ? withLeading : `${withLeading}/`;
}

function buildQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** The problem+json content type, plus plain JSON, because proxies rewrite it. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const contentType = response.headers.get('Content-Type') ?? '';
  if (contentType.includes('json')) {
    try {
      return await response.json();
    } catch {
      return undefined;
    }
  }
  const text = await response.text();
  return text === '' ? undefined : text;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly config: ApiClientConfig;
  private readonly fetchImpl: typeof fetch;
  /** One refresh at a time; every 401 in flight awaits the same promise. */
  private refreshInFlight: Promise<StoredSession | null> | null = null;

  /**
   * Set SYNCHRONOUSLY the instant a refresh begins, and the reason a refresh storm is
   * now impossible rather than merely unlikely. See `runRefresh`.
   */
  private refreshing = false;

  constructor(config: ApiClientConfig) {
    this.config = config;
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.fetchImpl = config.fetchImpl ?? ((...args) => fetch(...args));
  }

  get session(): SessionStore {
    return this.config.session;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const correlationId = newCorrelationId();
    // An `exact` path is absolute and slashless; a router resource hangs off the
    // audience root and always gets its slash.
    const root = options.exact ? '' : this.baseUrl;
    const wantsSlash = options.trailingSlash ?? !options.exact;
    const url = `${root}${normalisePath(path, wantsSlash)}${buildQuery(options.query)}`;

    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      'X-Correlation-Id': correlationId,
      ...options.headers,
    };

    // The token THIS request goes out carrying. Remembered because a 401 that arrives
    // after somebody else rotated the token says nothing about the session -- see the
    // retry branch below, which is the difference between one refresh and a storm.
    let usedToken: string | undefined;

    if (!options.anonymous) {
      let stored = this.config.session.read();

      // Refresh proactively rather than paying a guaranteed 401 first -- but NEVER from
      // inside the refresh request itself.
      //
      // `refreshSession` posts to the refresh route through this same method, carrying a
      // bearer, so without the `_retried` guard it re-entered this branch: the session is
      // still expiring, so it refreshed again, and that refresh refreshed again. Each
      // level puts a real request on the wire, so an expired session produced an endless
      // stream of `auth/refresh` 401s with no other call ever sent -- which is exactly
      // what production showed, 150 of them in 150 log lines and not one `/me`.
      //
      // The single-flight guard cannot stop it: `refreshInFlight` is assigned only after
      // `config.refresh(...)` returns, and the recursion happens synchronously inside that
      // call, while the field is still null.
      if (stored && isExpiring(stored) && this.config.refresh && !options._retried) {
        const refreshed = await this.runRefresh(stored);
        if (!refreshed) {
          // The session could not be renewed, so it is over. Ending it here drops the
          // caller at the sign-in screen. Sending the request anyway would earn a 401 and
          // start the retry dance for a session already known to be finished -- which is
          // how "expired" used to present as a spinner that never resolved.
          this.endSession();
          throw new UnauthenticatedError('Your session has ended.', {
            status: 401,
            code: 'session_expired',
            detail: 'Your session has ended. Please sign in again.',
            correlationId,
          });
        }
        stored = refreshed;
      }

      if (stored?.accessToken) {
        headers.Authorization = `Bearer ${stored.accessToken}`;
        usedToken = stored.accessToken;
      }
    }

    if (options.ifMatch !== undefined) headers['If-Match'] = String(options.ifMatch);
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

    let body: BodyInit | undefined;
    if (options.formData) {
      // Let the browser set the multipart boundary.
      body = options.formData;
    } else if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: options.method ?? 'GET',
        headers,
        body,
        signal: options.signal,
        credentials: 'omit',
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new NetworkError('The request could not be sent.', cause);
    }

    if (response.ok) {
      if (options.asBlob) return (await response.blob()) as T;
      return (await readBody(response)) as T;
    }

    const payload = await readBody(response);
    const error = toApiError(response.status, payload, response.headers, correlationId);

    // One retry after a successful refresh; then it is a real 401.
    if (error instanceof UnauthenticatedError && !options.anonymous && !options._retried) {
      const current = this.config.session.read();

      // Somebody else refreshed while this request was in flight, so this 401 is about a
      // token that has already been replaced -- not about the session. Replay with the
      // one that is current now.
      //
      // Refreshing again here is what turned a single page load into an endless storm.
      // Refresh ROTATES: the server kills the old token the instant it issues the new one
      // (see `refresh_session`, which is deliberate -- it bounds a stolen token). Several
      // queries leave together carrying the same token; the first 401 rotates it, which
      // invalidates the token the others are still holding; each of those then refreshes,
      // invalidating whatever the next retry is carrying. `refreshInFlight` cannot help,
      // because these refreshes never overlap in time: each begins after the last
      // finished. The observed result was thousands of refreshes, none succeeding, and a
      // spinner that never resolved -- the give-up branch is unreachable from a request
      // marked `_retried`.
      if (current?.accessToken && current.accessToken !== usedToken) {
        return this.request<T>(path, { ...options, _retried: true });
      }

      if (current && this.config.refresh) {
        const refreshed = await this.runRefresh(current);
        if (refreshed) {
          return this.request<T>(path, { ...options, _retried: true });
        }
      }
      this.endSession();
    }

    throw error;
  }

  private async runRefresh(current: StoredSession): Promise<StoredSession | null> {
    if (!this.config.refresh) return null;

    // A refresh already published: every concurrent caller awaits that one. This is the
    // ordinary case and the dedup that was always intended.
    if (this.refreshInFlight) return this.refreshInFlight;

    // RE-ENTRANCY, refused structurally.
    //
    // `refreshInFlight` alone cannot catch this. It is assigned only after
    // `config.refresh(...)` RETURNS a promise, and that function runs synchronously as far
    // as its first await -- which is inside this same client, issuing the refresh request.
    // Anything re-entering during that window saw a null field and started another
    // refresh, which re-entered again. One expired session produced an unbounded stream of
    // requests that way, twice, through two different call paths.
    //
    // Guarding the paths was the wrong fix and I made it twice: it holds only until
    // somebody adds a third. This holds whatever the path, including ones not yet written.
    if (this.refreshing) return null;

    this.refreshing = true;
    this.refreshInFlight = this.config
      .refresh(current)
      .then((next) => {
        if (next) this.config.session.write(next);
        return next;
      })
      .catch(() => null)
      .finally(() => {
        this.refreshInFlight = null;
        this.refreshing = false;
      });

    return this.refreshInFlight;
  }

  /** Clear local state and notify the app. Does not call the server. */
  endSession(): void {
    this.config.session.clear();
    this.config.onUnauthenticated?.();
  }

  get<T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<T>(path, { ...options, method: 'GET' });
  }
  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }
  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }
  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }
  /**
   * DELETE, optionally with a body.
   *
   * A body on DELETE is unusual but correct here and permitted by HTTP: deleting a
   * matter, client or lead requires a reason that goes on the permanent audit record.
   * The alternative was a query string, and a reason is free text a person writes --
   * it can name the client, and personal data does not belong in a URL, where it is
   * logged by every proxy on the way.
   */
  delete<T>(
    path: string,
    options?: Omit<RequestOptions, 'method' | 'body'> & { body?: unknown },
  ) {
    const { body, ...rest } = options ?? {};
    return this.request<T>(path, { ...rest, method: 'DELETE', body });
  }

  /**
   * Upload with progress. `fetch` cannot report upload progress, so this one
   * path uses XMLHttpRequest. Errors are normalised to the same `ApiError` tree.
   */
  upload<T>(
    path: string,
    formData: FormData,
    opts: {
      onProgress?: (percent: number) => void;
      signal?: AbortSignal;
      idempotencyKey?: string;
    } = {},
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const correlationId = newCorrelationId();
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.baseUrl}${normalisePath(path)}`);
      xhr.setRequestHeader('Accept', 'application/json, application/problem+json');
      xhr.setRequestHeader('X-Correlation-Id', correlationId);
      if (opts.idempotencyKey) xhr.setRequestHeader('Idempotency-Key', opts.idempotencyKey);
      const stored = this.config.session.read();
      if (stored?.accessToken) xhr.setRequestHeader('Authorization', `Bearer ${stored.accessToken}`);

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && opts.onProgress) {
          opts.onProgress(Math.round((event.loaded / event.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        let parsed: unknown;
        try {
          parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
        } catch {
          parsed = xhr.responseText;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as T);
        } else {
          const headers = new Headers();
          reject(toApiError(xhr.status, parsed, headers, correlationId));
        }
      });
      xhr.addEventListener('error', () =>
        reject(new NetworkError('The upload could not be sent.')),
      );
      xhr.addEventListener('abort', () =>
        reject(new ApiError('Upload cancelled.', { status: 0, code: 'aborted' })),
      );
      opts.signal?.addEventListener('abort', () => xhr.abort());

      xhr.send(formData);
    });
  }
}
